import { useEffect, useState } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { RoleResolver } from './roles';

export type IdentityRoles = {
  loading: boolean;
  isAdmin: boolean;
  isTeacher: boolean;
  isResearcher: boolean;
  isStudent: boolean;
  /** Convenience: true for admins or teachers, false otherwise. */
  isTeacherOrAdmin: boolean;
  /** Convenience: true for any non-student role (admin / umbrella / scope-wide reader). */
  isStaff: boolean;
  ownershipEntityRefs: string[];
};

/**
 * Resolves the current user's role from the Backstage identity JWT,
 * driven by the `permission.rbac.*` config block (see
 * kustomize/base/config/app-config.yaml).
 *
 * Returns a flat, synchronous-looking state object so the caller can
 * decide at render time whether to show/hide UI (tabs, cards, buttons).
 *
 * Note: this is a UI convenience; authoritative authorization is
 * enforced server-side by the catalog permission policy.
 */
export const useIdentityRoles = (): IdentityRoles => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const [state, setState] = useState<IdentityRoles>({
    loading: true,
    isAdmin: false,
    isTeacher: false,
    isResearcher: false,
    isStudent: false,
    isTeacherOrAdmin: false,
    isStaff: false,
    ownershipEntityRefs: [],
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const refs = identity.ownershipEntityRefs ?? [];
        const resolver = new RoleResolver(configApi);
        const admin = resolver.isAdmin(refs);
        const teacher = resolver.isUmbrellaReader(refs);
        const researcher = resolver.isScopeWideReader(refs);
        const student = resolver.isStudent(refs);
        if (mounted) {
          setState({
            loading: false,
            isAdmin: admin,
            isTeacher: teacher,
            isResearcher: researcher,
            isStudent: student,
            isTeacherOrAdmin: admin || teacher,
            isStaff: resolver.isStaff(refs),
            ownershipEntityRefs: refs,
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('useIdentityRoles: failed to resolve identity', e);
        }
        if (mounted) {
          setState(s => ({ ...s, loading: false }));
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, configApi]);

  return state;
};
